import { useEffect, useRef } from "react";
import { ArraySchema, ObjectSchema, Output, safeParse } from "valibot";

import { appSettingsStorage } from "./constants";
import { logParsedIssues } from "./functions";

type ErrorWithPayload = Error & {
  response: Response;
  status: number;
  payload?: {
    error: string;
  };
};

type ErrorWithUrl = Error & {
  url: string;
};

type ResponseSchema = ObjectSchema<any> | ArraySchema<any>;

type RequestOptions<T extends ResponseSchema | undefined> = RequestInit & {
  schema?: T;
};

const checkStatus = (response: Response) => {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  const error: ErrorWithPayload = {
    ...new Error(response.statusText),
    response,
    status: response.status,
  };

  return response.text().then(text => {
    let json;
    try {
      json = JSON.parse(text);
    } catch {
      error.payload = {
        error: response.status === 404 ? "404: Not found" : text,
      };
      throw error;
    }
    error.payload =
      typeof json === "object"
        ? json
        : {
            error: `${json}`,
          };
    throw error;
  });
};

export const fetchRequest = (url: string, options: RequestInit) =>
  fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "Base-Origin": appSettingsStorage.baseOrigin,
      ...options.headers,
    },
  }).catch(err => {
    const error: ErrorWithUrl = {
      ...new Error(err),
      url,
    };
    // eslint-disable-next-line no-console
    console.error(err);
    throw error;
  });

// TODO: Get rid of | any as returning value
export const request = <T extends ResponseSchema>(
  url: string,
  options: RequestOptions<T>,
): Promise<Output<T>> =>
  fetchRequest(url, options)
    .then(checkStatus)
    .then(async response => response.json())
    .then(json => {
      const { schema } = options;
      if (!schema) {
        return json;
      }
      const result = safeParse(schema, json);
      if (result.success) {
        return result.output;
      }
      /* eslint-disable no-console */
      console.error(
        "\u001B[37m\u001B[5m\u001B[41m%s\u001B[0m",
        "Please report screenshot of this issue if you see it",
        url,
      );
      logParsedIssues(result.issues);
      /* eslint-enable no-console */
      // We still should return data hoping it won't break logic
      return json as Output<typeof schema>;
    });

const getServerUrl = (url: string) =>
  `${appSettingsStorage.serviceBaseUrl}${url}`;

export const requestBackend = <T extends ResponseSchema>(
  url: string,
  options: RequestInit & { schema?: T } = {},
) => request<T>(getServerUrl(url), options);

export const useAbortController = () => {
  const abortControllerRef = useRef(new AbortController());
  useEffect(
    () => () => {
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
    },
    [abortControllerRef],
  );
  return abortControllerRef.current;
};
